<script setup lang="ts">
import BaseButton from '../baseButton/BaseButton.vue';
import RangeDateTimePicker from '../rangeDateTimePicker/RangeDateTimePicker.vue';
import { Props, setup, Emit } from './quickFilter.setup';

const props = defineProps<Props>();
const emit = defineEmits<Emit>();
const {
  t,

  filterStates,
  filterCount,

  setCalendarFilter,
  setOneButtonFilter,
  onClickClear,
  getFilterLabel,
} = setup(props, emit);
</script>

<template>
  <h-stack
    class="quick-filter"
    :spacing="8"
  >
    <h-stack
      class="quick-filter__title"
      :spacing="4"
    >
      <ev-icon
        icon="icon-filter-list"
        size="small"
        :class="['quick-filter__title-icon', { 'is-active': filterCount > 0 }]"
      />
      <span :class="['quick-filter__title-label', { 'is-active': filterCount > 0 }]">
        {{ t('WORD.FILTER') }}
      </span>
      <span
        v-if="filterCount > 0"
        class="quick-filter__title-count"
      >
        {{ filterCount }}
      </span>
    </h-stack>

    <div class="quick-filter__divider" />

    <h-stack
      :spacing="8"
      wrap="wrap"
      class="quick-filter__list"
    >
      <template
        v-for="item in quickFilterList"
        :key="item"
      >
        <template v-if="item.type === 'calendar'">
          <h-stack
            v-if="!filterStates[item.id].use"
            class="quick-filter__calendar"
          >
            <range-date-time-picker
              :id="item.id"
              v-model:data="filterStates[item.id].filterValue as [string, string]"
              class="quick-filter__date-picker"
              :time-unit="item.timeUnit"
              :time-format="item.timeFormat"
              :quick-date-list="item.shortCuts"
              :quick-custom-date-list="item.quickCustomDateList"
              :placeholder="item.label"
              :hide-prefix-icon="true"
              :hide-input="true"
              :allow-future-dates="item.allowFutureDates"
              @update:data="(v) => setCalendarFilter(v, item.id)"
            />
          </h-stack>
        </template>

        <template v-if="item.type === 'oneOf'">
          <h-stack
            v-if="!filterStates[item.id].use"
            class="quick-filter__one-button"
          >
            <base-button
              size="small"
              shape="border"
              class="quick-filter__button"
              @click="setOneButtonFilter(item.id)"
            >
              <span class="quick-filter__button-label">{{ item.label }}</span>
            </base-button>
          </h-stack>
        </template>

        <h-stack
          v-if="filterStates[item.id].use"
          :spacing="4"
          class="quick-filter__filtered-item"
        >
          <span class="quick-filter__filtered-item-label">{{ item.label }}</span>

          <template v-if="item.type === 'calendar'">
            <div class="quick-filter__divider is-short" />
            <span class="quick-filter__filtered-item-label">
              {{ getFilterLabel(item) }}
            </span>
          </template>

          <base-button
            type="icon"
            size="small"
            @click="onClickClear(item.id)"
          >
            <ev-icon icon="icon-close" />
          </base-button>
        </h-stack>
      </template>
    </h-stack>
  </h-stack>
</template>

<style scoped lang="scss">
.quick-filter {
  width: 100%;
  height: auto;
  min-height: 30px;
  padding-top: 8px;
  border-radius: 4px;

  &__divider {
    width: 1px;
    height: 20px;
    background-color: var(--color-gray-04-06);

    &.is-short {
      height: 16px;
    }
  }

  &__filtered-item {
    height: 24px;
    padding: 6px 8px;
    border-radius: 2px;
    background: var(--color-gray-05-03);
    line-height: 1px;

    &-label {
      @include typography('body-2');
    }
  }

  &__title {
    &-label {
      white-space: nowrap;

      @include typography('button-medium');
      &.is-active {
        color: var(--color-blue-05-05);
      }
    }

    &-icon {
      &.is-active {
        background-color: var(--color-blue-05-05);
      }
    }

    &-count {
      display: flex;
      gap: 2px;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      padding: 4px 8px;
      border-radius: 29px;
      background-color: var(--color-blue-05-05);
      color: var(--color-gray-00-00);
    }
  }
  &__date-picker {
    width: 100%;
    min-width: 160px;
    background-color: var(--color-gray-12-00);

    :deep(.ev-input) {
      height: 24px !important;
      cursor: pointer;

      &::placeholder {
        opacity: 1;
        color: var(--main-font-color) !important;
      }
    }
  }
  &__button {
    height: 24px !important;

    &-label {
      line-height: normal !important;

      @include typography('body-2');
    }
  }
}
</style>
